<template>
  <Section
    :id="id"
    :header="t('referenceData.referenceNumbers', 2)"
    class="ref-nums-section"
  >
    <ReferenceNumberViewEdit
      v-for="x in filteredReferenceNumbers"
      :id="`${id}-${x.internalValueIndex}-${x.refNum.typeId}`"
      :key="x.refNum.id ?? `${x.refNum.typeNaturalId}-${x.internalValueIndex}`"
      :model-value="x.refNum"
      :client-types="availableClientReferenceNumberTypes.data"
      :editable="editable"
      @delete="() => onReferenceNumberDelete(x)"
      @update:model-value="(refNum) => onReferenceNumbersUpdate(refNum, x)"
    />

    <ReferenceNumberViewEdit
      v-for="(referenceNum, index) in readonlyRefNums"
      :id="`${id}-${referenceNum.typeName}`"
      :key="`${referenceNum.typeName}-${index}`"
      :model-value="referenceNum"
      :editable="false"
    />

    <NewReferenceNumberEdit
      :resource-id="resourceId"
      :client-types="availableClientReferenceNumberTypes.data"
      :type-options="typeOptions"
      :editable="editable"
      :level="level"
      class="align-self-end"
      @reference-number-create="onReferenceNumberCreate"
    />
  </Section>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDirtyState } from '@rygen/rygen-ui/composables';
import {
  DomainReference,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { useReferenceNumberTypes } from '@/composables';
import { ReferenceNumber } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import NewReferenceNumberEdit from '@/components/referenceNumber/NewReferenceNumberEdit.vue';
import ReferenceNumberViewEdit from '@/components/referenceNumber/ReferenceNumberViewEdit.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

type FilteredReferenceNumberWithInternalValueIndex = {
  refNum: ReferenceNumber;
  internalValueIndex: number;
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<ReferenceNumber[]>,
    required: false,
    default: () => [],
  },
  resourceId: {
    type: Number,
    required: true,
  },
  extras: {
    type: Array as PropType<{ name: string; value: string }[]>,
    required: false,
    default: () => [],
  },
  level: {
    type: String as PropType<ReferenceNumberTypeLevel>,
    default: '',
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, resourceId, extras, level, editable } = toRefs(props);
const emit = defineEmits<{
  (
    event: 'change',
    dirtyState: {
      isDirty: boolean;
      numDirty: number;
    },
  ): void;
  (event: 'update:model-value', newVal: ReferenceNumber[]): void;
}>();

const { dirtyState, itemAdded, itemDeleted, itemEdited, resetDirtyState } =
  useDirtyState(modelValue.value);
const { t } = useI18n();
const { owningClient } = useParentEntityStore();
const { availableClientReferenceNumberTypes, loadReferenceNumberTypes } =
  useReferenceNumberTypes(toRef(() => [level.value]));

const internalReferenceNumbers: Ref<ReferenceNumber[]> = ref(modelValue.value);
const totalRefNums = ref(internalReferenceNumbers.value.length);

// Filter out already used types unless they are configured with allowMultiple
const typeOptions = computed(() => {
  return (
    availableClientReferenceNumberTypes.value.data
      ?.filter((clientType) => {
        const existingRefNum = internalReferenceNumbers.value.find(
          (r) =>
            r.type?.naturalId &&
            r.type.naturalId === clientType.referenceNumberType?.naturalId,
        );
        return !existingRefNum || clientType.allowMultiple;
      })
      ?.map((clientType) => clientType.referenceNumberType)
      ?.filter(notNull) ?? []
  );
});

const readonlyRefNums = computed(() => {
  return extras.value
    .filter((e) => e.name && e.value)
    .map((e) => new ReferenceNumber({ typeName: e.name, value: e.value }));
});

const filteredReferenceNumbers: Ref<
  FilteredReferenceNumberWithInternalValueIndex[]
> = computed(() => {
  const referenceNumbersWithInternalValueIndex: FilteredReferenceNumberWithInternalValueIndex[] =
    internalReferenceNumbers.value.map((refNum, internalValueIndex) => ({
      refNum,
      internalValueIndex,
    }));
  let filteredReferenceNumbers = referenceNumbersWithInternalValueIndex;
  if (level.value === ReferenceNumberTypeLevel.SHIPMENT) {
    filteredReferenceNumbers = filteredReferenceNumbers.filter(
      (x) => x.refNum.source?.type === ReferenceNumberTypeLevel.SHIPMENT,
    );
  }
  return filteredReferenceNumbers;
});

const onReferenceNumbersUpdate = (
  updatedRefNum: ReferenceNumber,
  { internalValueIndex }: FilteredReferenceNumberWithInternalValueIndex,
) => {
  itemEdited(
    internalValueIndex,
    updatedRefNum.value !==
      internalReferenceNumbers.value[internalValueIndex].value,
  );
  internalReferenceNumbers.value[internalValueIndex] = updatedRefNum;
};

const onReferenceNumberCreate = (refNum: ReferenceNumber) => {
  internalReferenceNumbers.value.push(refNum);
  itemAdded();
};

const onReferenceNumberDelete = ({
  internalValueIndex,
}: FilteredReferenceNumberWithInternalValueIndex) => {
  itemDeleted(internalValueIndex, internalValueIndex < totalRefNums.value);
  internalReferenceNumbers.value.splice(internalValueIndex, 1);
};

watch(modelValue, (referenceNumbers: ReferenceNumber[]) => {
  internalReferenceNumbers.value = referenceNumbers;
  resetDirtyState(referenceNumbers);
  totalRefNums.value = referenceNumbers.length;
});

watch(
  internalReferenceNumbers,
  (referenceNumbers: ReferenceNumber[]) => {
    if (dirtyState.value.isDirty) {
      emit('change', dirtyState.value);
      emit('update:model-value', referenceNumbers);
    }
  },
  { deep: true },
);

watch(
  owningClient,
  (newVal: DomainReference | undefined) => {
    if (newVal && editable.value && level.value) {
      loadReferenceNumberTypes({ level: level.value });
    }
  },
  { immediate: true },
);
</script>

<style>
.ref-nums-section {
  .ref-nums-section-row {
    margin-bottom: var(--sp-3);

    .ref-nums-section-row-label {
      color: var(--p-gray-600);
      font-size: var(--label-font-size);
      font-weight: 500;
      text-transform: uppercase;
    }

    .ref-nums-section-row-actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--sp-3);
    }
  }
}
</style>
